import type { FeatureFlag, GameId, Lobby } from './types';

/**
 * @fileOverview Cross-language play ("Multilingual Mode").
 *
 * Normally a lobby has ONE language: the host picks it and every player reads
 * the same words. That breaks a mixed group - a German speaker and a Hebrew
 * speaker cannot share a room unless one of them plays in a language they do
 * not read.
 *
 * Multilingual Mode splits the two ideas apart. The lobby still has a language
 * (it drives the UI chrome and is the language content is authored in), but
 * each player also picks the language THEY read game content in. Supported
 * games then ship every player their own translation of the same round, so the
 * round is shared even though the words are not.
 *
 * Only games whose mechanic survives translation are listed here - see
 * MULTILINGUAL_GAME_IDS.
 */

export const MULTILINGUAL_FEATURE_ID = 'multilingual_play';

/**
 * Games where a translated round is still the SAME round.
 *
 * The test is whether the thing players exchange is language-free:
 *  - pictionary     - the drawing is the message; only the prompt is words.
 *  - charades       - the acting is the message; only the prompt is words.
 *  - wouldYouRather - players answer "A or B"; the dilemma is read privately.
 *
 * Games where players must exchange text with EACH OTHER (Link Logic clues,
 * Quick Minds typed answers, One Word Story, Imposter discussion) are
 * deliberately absent: translating the content there would still leave the
 * players unable to understand one another, which is worse than an honest
 * "this game needs one shared language".
 */
export const MULTILINGUAL_GAME_IDS = ['pictionary', 'charades', 'wouldYouRather'] as const;
export type MultilingualGameId = (typeof MULTILINGUAL_GAME_IDS)[number];

/**
 * Ceiling on distinct languages translated per round. Every extra language is
 * more model output on the critical path of a round start, and a party with
 * more genuinely distinct languages than this is not a real room.
 */
export const MULTILINGUAL_MAX_LANGUAGES = 5;

export const DEFAULT_LANGUAGE = 'en';

/** language id -> translated string. Always safe to read with pickTranslation. */
export type TranslationBundle = Record<string, string>;

type MultilingualLobby = Partial<
  Pick<Lobby, 'currentGame' | 'language' | 'multilingualMode' | 'playerLanguages' | 'players' | 'isOpenLobby' | 'matchmakingMode'>
>;

/**
 * Public matchmaking rooms (Game Finder "open" rooms and 1v1 matches) seat
 * strangers who never agreed on a language, so the host's pick must not become
 * everyone's: a Hebrew host used to hand every joiner Hebrew chrome and Hebrew
 * Pictionary words. These rooms are therefore ALWAYS in per-player mode and
 * every seat reads in its own site language.
 */
export function isOpenMatchmakingLobby(lobby?: MultilingualLobby | null): boolean {
  return !!lobby?.isOpenLobby && (lobby.matchmakingMode === 'open' || lobby.matchmakingMode === 'one_on_one' || lobby.matchmakingMode === 'escape_room');
}

export function supportsMultilingualPlay(gameId?: string | null): gameId is MultilingualGameId {
  return !!gameId && (MULTILINGUAL_GAME_IDS as readonly string[]).includes(gameId);
}

export function normalizeLanguage(language?: string | null): string {
  const trimmed = String(language || '').trim();
  return trimmed || DEFAULT_LANGUAGE;
}

/**
 * The host switched the room into per-player languages, or the room is a
 * public matchmaking room (always per-player, see isOpenMatchmakingLobby).
 */
export function isMultilingualEnabled(lobby?: MultilingualLobby | null): boolean {
  return !!lobby?.multilingualMode || isOpenMatchmakingLobby(lobby);
}

/**
 * The Firestore update that seats a player in a public room under their own
 * site language. Empty when nothing has to change (not a public room, no
 * player, or the seat already reads that language), so callers can spread it
 * into a join write or run it as a cheap idempotent sync.
 */
export function getOpenLobbyPlayerLanguageUpdates({
  lobby,
  uid,
  siteLanguage,
}: {
  lobby?: MultilingualLobby | null;
  uid?: string | null;
  siteLanguage?: string | null;
}): Record<string, string> {
  if (!uid || !isOpenMatchmakingLobby(lobby)) return {};
  const desired = normalizeLanguage(siteLanguage);
  if (normalizeLanguage(lobby?.playerLanguages?.[uid]) === desired && lobby?.playerLanguages?.[uid]) return {};
  return { [`playerLanguages.${uid}`]: desired };
}

/**
 * The mode is on AND the game on screen can honour it. Every game-side branch
 * keys off this, so an unsupported game behaves exactly as it did before.
 */
export function isMultilingualRound(lobby?: MultilingualLobby | null): boolean {
  return isMultilingualEnabled(lobby) && supportsMultilingualPlay(lobby?.currentGame);
}

/**
 * The language a single player reads game content in. Falls back to the lobby
 * language, so a player who never picked one is exactly where they used to be.
 */
export function getPlayerLanguage(lobby?: MultilingualLobby | null, uid?: string | null): string {
  const lobbyLanguage = normalizeLanguage(lobby?.language);
  if (!isMultilingualEnabled(lobby) || !uid) return lobbyLanguage;
  return normalizeLanguage(lobby?.playerLanguages?.[uid] || lobbyLanguage);
}

/**
 * Every language the current room actually needs, deduped and capped.
 *
 * Deterministic on purpose: the server builds a round's translations from this
 * list and each client picks its own entry out of the result, so both sides
 * must derive the same list from the same lobby snapshot. Ordering is lobby
 * language first, then by how many players read it (most-read first), ties
 * broken alphabetically.
 */
export function getMultilingualLanguages(lobby?: MultilingualLobby | null): string[] {
  const lobbyLanguage = normalizeLanguage(lobby?.language);
  if (!isMultilingualEnabled(lobby)) return [lobbyLanguage];

  const record = lobby?.playerLanguages || {};
  const playerIds = Object.keys(lobby?.players || {});
  // Players who have left should not keep paying for their language, but a
  // lobby snapshot without a players map (tests, partial reads) still has to
  // produce something usable.
  const seats = playerIds.length > 0 ? playerIds : Object.keys(record);

  const counts = new Map<string, number>();
  for (const uid of seats) {
    const language = normalizeLanguage(record[uid] || lobbyLanguage);
    if (language === lobbyLanguage) continue;
    counts.set(language, (counts.get(language) || 0) + 1);
  }

  const ranked = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([language]) => language);

  return [lobbyLanguage, ...ranked].slice(0, MULTILINGUAL_MAX_LANGUAGES);
}

/**
 * True when the room genuinely spans languages. Used to skip translation work
 * (and the cross-language UI) in a room where the mode is on but everyone
 * happens to read the same thing.
 */
export function hasCrossLanguagePlayers(lobby?: MultilingualLobby | null): boolean {
  return getMultilingualLanguages(lobby).length > 1;
}

/**
 * Reads a translation with the full fallback chain. A missing or blank entry
 * never blanks the UI - the caller's source string wins.
 */
export function pickTranslation(
  bundle: TranslationBundle | null | undefined,
  language?: string | null,
  fallback = '',
): string {
  if (!bundle) return fallback;
  const wanted = normalizeLanguage(language);
  const direct = bundle[wanted];
  if (typeof direct === 'string' && direct.trim()) return direct;

  // "pt-BR" happily reads "pt" and vice versa when only one of them exists.
  const base = wanted.split('-')[0];
  if (base !== wanted) {
    const baseHit = bundle[base];
    if (typeof baseHit === 'string' && baseHit.trim()) return baseHit;
  }
  const regional = Object.keys(bundle).find(
    (key) => key.split('-')[0] === base && typeof bundle[key] === 'string' && bundle[key].trim(),
  );
  if (regional) return bundle[regional];

  return fallback;
}

/**
 * Normalizes a raw translation map: drops blanks, and guarantees the source
 * language maps to the source string so pickTranslation always resolves.
 */
export function buildTranslationBundle(input: {
  source: string;
  sourceLanguage?: string | null;
  translations?: Record<string, string | null | undefined> | null;
}): TranslationBundle {
  const bundle: TranslationBundle = {};
  for (const [language, value] of Object.entries(input.translations || {})) {
    const text = String(value || '').trim();
    if (!text) continue;
    bundle[normalizeLanguage(language)] = text;
  }
  const source = String(input.source || '').trim();
  if (source) bundle[normalizeLanguage(input.sourceLanguage)] = source;
  return bundle;
}

/**
 * Every distinct string in a bundle. A guess is judged against all of them, so
 * a Spanish speaker typing "manzana" and a German typing "Apfel" both decode
 * the same drawing.
 */
export function getTranslationVariants(
  bundle: TranslationBundle | null | undefined,
  fallback?: string | null,
): string[] {
  const seen = new Set<string>();
  const variants: string[] = [];
  const push = (value?: string | null) => {
    const text = String(value || '').trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    variants.push(text);
  };
  push(fallback);
  for (const value of Object.values(bundle || {})) push(value);
  return variants;
}

export interface MultilingualAccess {
  /** Flag is in maintenance - the mode is off for everyone, no deploy needed. */
  disabled: boolean;
  /** Flag is tester-gated and this viewer is not one. */
  testersOnly: boolean;
  canUse: boolean;
}

export function getMultilingualAccess(input: {
  flagStatus: FeatureFlag['status'];
  isAdmin?: boolean;
  isTester?: boolean;
}): MultilingualAccess {
  const disabled = input.flagStatus === 'maintenance';
  const testersOnly = input.flagStatus === 'testers';
  const entitled = !!input.isAdmin || !!input.isTester || !testersOnly;
  return { disabled, testersOnly, canUse: !disabled && entitled };
}

/**
 * Turning the mode off has to clear the per-player picks too, otherwise a
 * later re-enable silently restores languages nobody re-chose.
 */
export function getMultilingualToggleUpdates(enabled: boolean): Record<string, unknown> {
  return enabled ? { multilingualMode: true } : { multilingualMode: false, playerLanguages: {} };
}

/** Game ids as a plain array, for UI that lists what the mode covers. */
export function getMultilingualGameIds(): GameId[] {
  return [...MULTILINGUAL_GAME_IDS] as GameId[];
}
